import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { AnimatedStatementText } from '$partials/ui/index.js';
import { Header } from '$partials/global/index.js';
import { IconChevronSprite } from '$partials/icons/index.js';
import { ModuleTextTicker } from '$partials/modules/individual/index.js';

gsap.registerPlugin(ScrollTrigger);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const HeaderLanding = (props) => {
    // references
    const containerRef = useRef();
    const timerRef = useRef();
    const splitRef = useRef();
    const timelineRef = useRef();

    // state
    const [ isOpen, setIsOpen ] = useState(false);
    const [ windowSize, setWindowSize ] = useState({ height: undefined, width: undefined });

    // methods
    const createAnimation = () => {
        if (containerRef.current) {
            // create timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    // markers: true,
                    start: 'top center',
                    trigger: containerRef.current
                }
            });

            timelineRef.current = tl;

            // create initial reveal of split text content
            tl.to(
                containerRef.current.querySelectorAll('.animation__mask-slide-in'),
                {
                    duration: 0.1,
                    opacity: 1,
                    overwrite: true
                },
                0
            );

            // create split text object
            const split = SplitText.create(
                containerRef.current.querySelectorAll('.animation__mask-slide-in'),
                {
                    linesClass: 'animation__line',
                    mask: 'lines',
                    onSplit: (self) => {
                        // on complete of split, create and add tween to timeline
                        const to = tl.to(
                            self.lines,
                            {
                                duration: 0.8,
                                ease: 'power3',
                                overwrite: true,
                                stagger: 0.15,
                                y: '0%'
                            },
                            0
                        );

                        return to;
                    },
                    type: 'lines'
                }
            );

            splitRef.current = split;

            // create scroll up tween and add to timeline
            tl.to(
                containerRef.current.querySelectorAll('.animation__scroll-up'),
                {
                    duration: 3,
                    ease: 'power3',
                    overwrite: true,
                    y: (i, target) => {
                        return (target.offsetHeight - (target.firstChild.offsetHeight)) * -1;
                    }
                },
                0
            );

            tl.to(
                containerRef.current.querySelectorAll('.animation__scale-blur-fade-in'),
                {
                    duration: 1,
                    ease: 'smooth',
                    filter: 'blur(0px)',
                    opacity: 1,
                    overwrite: true,
                    scale: 1
                },
                0
            );

            tl.to(
                containerRef.current.querySelectorAll('.animation__fade-in'),
                {
                    delay: 1,
                    duration: 1,
                    ease: 'power3',
                    opacity: 1,
                    overwrite: true
                },
                0
            );
        }
    };

    useEffect(() => {
        const handleResize = () => {
            // only recreate animation when resize width changes
            if (window.innerWidth !== windowSize.width) {
                // clear split & timeline
                splitRef?.current?.revert?.();
                timelineRef?.current?.revert?.();

                // debounce split/timeline creation
                timerRef.current && clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setWindowSize({ height: window.innerHeight, width: window.innerWidth });
                    createAnimation();
                }, 100);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [ windowSize.width ]);

    // render
    return (
        <section
            className={props?.headerBackgroundImage ? null : 'bg-grey-off-black'}
            ref={containerRef}>
            <div className='relative overflow-hidden'>
                {/* header global */}
                <div className='absolute inset-0'>
                    <div className='relative z-40'>
                        <Header
                            bgStyle={isOpen ? 'white' : 'transparent-landing'}
                            isOpen={isOpen}
                            menu={props?.navigation}
                            setIsOpen={setIsOpen} />
                    </div>
                </div>

                {/* image */}
                {props?.headerBackgroundImage && (
                    <>
                        <img
                            alt=''
                            aria-hidden='true'
                            className='animation__scale-blur-fade-in absolute inset-0 h-full w-full object-center object-cover select-none pointer-events-none'
                            height={props.headerBackgroundImage.height}
                            loading='eager'
                            src={`${props?.headerBackgroundImage.src}?fm=jpg&q=80&fl=progressive&w=2880`}
                            width={props.headerBackgroundImage.width} />

                        <div className='animation__scale-blur-fade-in absolute inset-0 media-top-bottom-gradient' />
                    </>
                )}


                <div className='relative h-svh min-h-600 flex items-center py-178'>
                    <div className='absolute top-1/2 -translate-y-1/4 w-full pb-20'>
                        <div className='animated-statement animated-statement--white'>
                            <div className='layout-container w-full overflow-hidden'>
                                {/* eyebrow */}
                                {props?.eyebrow && (
                                    <div className='w-full sm:w-10/12 md:w-8/12 px-10 sm:px-5 md:px-10 mb-20'>
                                        <div className='type-style-7 break-words animated-statement__text'>
                                            <AnimatedStatementText>
                                                {props.eyebrow}
                                            </AnimatedStatementText>
                                        </div>
                                    </div>
                                )}

                                {/* main text */}
                                {props?.mainText && (
                                    <div className='w-full sm:w-10/12 md:w-8/12 px-10 sm:px-5 md:px-10'>
                                        <div className='type-style-1 break-words animated-statement__text'>
                                            <AnimatedStatementText>
                                                {props.mainText}
                                            </AnimatedStatementText>
                                        </div>
                                    </div>
                                )}

                                {/* subtext */}
                                {props?.subText && (
                                    <div className='w-full sm:w-10/12 md:w-7/12 px-10 sm:px-5 md:px-10 mt-20'>
                                        <div className='type-style-8 break-words animated-statement__text'>
                                            <AnimatedStatementText>
                                                {props.subText}
                                            </AnimatedStatementText>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className='layout-container w-full'>
                            {/* CTA */}
                            {(props?.ctaLabel && props?.ctaLink) && (
                                <div className='w-full sm:w-7/12 px-10 sm:px-5 md:px-10 mt-32'>
                                    <a
                                        className='button button--primary animation__fade-in'
                                        href={props.ctaLink}>
                                        <span>{props.ctaLabel}</span>
                                        <span className='button__icon button__icon--animate'>
                                            <IconChevronSprite />
                                        </span>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {props?.tickerText && (
                        <div className='absolute bottom-0 left-0 right-0'>
                            <ModuleTextTicker
                                nextTypename='landing'
                                previousTypename='landing'
                                text={props?.tickerText} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    eyebrow: PropTypes.string,
    headerBackgroundImage: PropTypes.object,
    mainText: PropTypes.string.isRequired,
    navigation: PropTypes.object.isRequired,
    subText: PropTypes.string,
    tickerText: PropTypes.string,
};

HeaderLanding.propTypes = propTypes;

export default HeaderLanding;
